import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ClickerSection } from './components/ClickerSection';
import { GardenSection } from './components/GardenSection';
import { MatchThreeGame } from './components/MatchThreeGame';
import { PlayerStats } from './components/PlayerStats';
import { UpgradeShop } from './components/UpgradeShop';
import { PowerUpShop } from './components/PowerUpShop';

import { PrestigeSection } from './components/PrestigeSection';

import CompanionManager from './components/CompanionManager';
import LevelProgressBar from './components/LevelProgressBar';
import BackgroundMusic from './components/BackgroundMusic';
import StatsOverlay from './components/StatsOverlay';
import PremiumAutoClicker from './components/PremiumAutoClicker';
import PremiumShop from './components/PremiumShop';
import CompanionArena from './components/CompanionArena';
import Footer from './components/Footer';
// import SettingsPage from './components/SettingsPage';
import { useGameStore } from './store/gameStore';
import { useStorageManager } from './hooks/useStorageManager';
import { gameBackupManager } from './utils/gameBackupManager';
import VideoRewardModal from './components/VideoRewardModal';
import PerformanceMonitor from './components/PerformanceMonitor';
import { Leaderboard } from './components/Leaderboard';

// Temporäre inline SettingsPage-Komponente
const SettingsPage: React.FC = () => {
  const { formatNumber, resetGame, coins, gems, playerLevel, prestige, settings, toggleMusic } = useGameStore();
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showExportData, setShowExportData] = useState(false);
  const [importData, setImportData] = useState('');
  const [showImportData, setShowImportData] = useState(false);

  // Spielstand exportieren
  const exportGameData = () => {
    try {
      const gameData = localStorage.getItem('garden-clicker-storage');
      if (gameData) {
        const blob = new Blob([gameData], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `garden-clicker-save-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setShowExportData(true);
        setTimeout(() => setShowExportData(false), 3000);
      }
    } catch (error) {
      console.error('Fehler beim Exportieren:', error);
    }
  };

  // Spielstand importieren
  const importGameData = () => {
    try {
      if (importData.trim()) {
        const parsedData = JSON.parse(importData);
        localStorage.setItem('garden-clicker-storage', JSON.stringify(parsedData));
        window.location.reload();
      }
    } catch (error) {
      alert('Ungültige Spielstand-Daten! Bitte überprüfe das Format.');
      console.error('Fehler beim Importieren:', error);
    }
  };

  // Spiel zurücksetzen
  const handleResetGame = () => {
    resetGame();
    setShowResetConfirm(false);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">⚙️ Einstellungen</h1>
        <p className="text-sm sm:text-base text-gray-600">Verwalte deine Spieleinstellungen und deinen Spielstand</p>
      </div>

      {/* Spielstand-Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 sm:p-6 border border-blue-200"
      >
        <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4 flex items-center">
          📊 Spielstand-Übersicht
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          <div className="text-center p-2 sm:p-0">
            <div className="text-lg sm:text-2xl font-bold text-green-600">{formatNumber(coins)}</div>
            <div className="text-xs sm:text-sm text-gray-600">💰 Münzen</div>
          </div>
          <div className="text-center p-2 sm:p-0">
            <div className="text-lg sm:text-2xl font-bold text-purple-600">{formatNumber(gems)}</div>
            <div className="text-xs sm:text-sm text-gray-600">💎 Diamanten</div>
          </div>
          <div className="text-center p-2 sm:p-0">
            <div className="text-lg sm:text-2xl font-bold text-blue-600">{playerLevel}</div>
            <div className="text-xs sm:text-sm text-gray-600">🏆 Level</div>
          </div>
          <div className="text-center p-2 sm:p-0">
            <div className="text-lg sm:text-2xl font-bold text-yellow-600">{prestige?.level || 0}</div>
            <div className="text-xs sm:text-sm text-gray-600">⭐ Prestige</div>
          </div>
        </div>
      </motion.div>

      {/* Audio-Einstellungen */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200 shadow-lg"
      >
        <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4 flex items-center">
          🎵 Audio-Einstellungen
        </h2>
        
        <div className="space-y-3 sm:space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 p-3 sm:p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
            <div className="flex items-center gap-3">
              <span className="text-xl sm:text-2xl">{settings.musicEnabled ? '🎵' : '🔇'}</span>
              <div>
                <div className="font-semibold text-gray-800 text-sm sm:text-base">Hintergrundmusik</div>
                <div className="text-xs sm:text-sm text-gray-600">
                  {settings.musicEnabled ? 'Musik ist aktiviert' : 'Musik ist deaktiviert'}
                </div>
              </div>
            </div>
            <motion.button
              onClick={toggleMusic}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-all duration-300 text-sm sm:text-base min-h-[44px] touch-manipulation ${
                settings.musicEnabled
                  ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-lg'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
              }`}
            >
              {settings.musicEnabled ? 'Ausschalten' : 'Einschalten'}
            </motion.button>
          </div>
          
          <div className="text-xs sm:text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
            💡 <strong>Tipp:</strong> Du kannst die Musik auch über den schwebenden Button unten rechts ein- und ausschalten.
          </div>
        </div>
      </motion.div>

      {/* Performance-Einstellungen */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200 shadow-lg"
      >
        <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4 flex items-center">
          ⚡ Performance-Einstellungen
        </h2>
        
        <div className="space-y-3 sm:space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 p-3 sm:p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-3">
              <span className="text-xl sm:text-2xl">{settings.performanceMonitorEnabled ? '⚡' : '📊'}</span>
              <div>
                <div className="font-semibold text-gray-800 text-sm sm:text-base">Performance-Monitor</div>
                <div className="text-xs sm:text-sm text-gray-600">
                  {settings.performanceMonitorEnabled 
                    ? 'Monitor ist aktiviert (zeigt FPS & Memory)' 
                    : 'Monitor ist deaktiviert'
                  }
                </div>
              </div>
            </div>
            <motion.button
              onClick={() => {
                const newSettings = { ...settings, performanceMonitorEnabled: !settings.performanceMonitorEnabled };
                localStorage.setItem('garden-clicker-storage', JSON.stringify({
                  ...JSON.parse(localStorage.getItem('garden-clicker-storage') || '{}'),
                  state: {
                    ...JSON.parse(localStorage.getItem('garden-clicker-storage') || '{}').state,
                    settings: newSettings
                  }
                }));
                window.location.reload();
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-all duration-300 text-sm sm:text-base min-h-[44px] touch-manipulation ${
                settings.performanceMonitorEnabled
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white shadow-lg'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
              }`}
            >
              {settings.performanceMonitorEnabled ? 'Deaktivieren' : 'Aktivieren'}
            </motion.button>
          </div>
          
          <div className="text-xs sm:text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
            💡 <strong>Info:</strong> Der Performance-Monitor zeigt FPS, Memory-Verbrauch und Performance-Tipps an. 
            Besonders nützlich bei hohen Levels oder Performance-Problemen.
          </div>
        </div>
      </motion.div>

      {/* Spielstand-Verwaltung */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200 shadow-lg"
      >
        <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4 flex items-center">
          💾 Spielstand-Verwaltung
        </h2>
        
        <div className="space-y-3 sm:space-y-4">
          {/* Export */}
          <div className="flex flex-col gap-3">
            <button
              onClick={exportGameData}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-4 sm:px-6 py-3 sm:py-3 rounded-lg font-medium transition-all duration-300 transform hover:scale-105 shadow-lg text-sm sm:text-base min-h-[44px] touch-manipulation"
            >
              📤 Spielstand exportieren
            </button>
            {showExportData && (
              <div className="text-green-600 text-xs sm:text-sm flex items-center justify-center sm:justify-start">
                ✅ Spielstand erfolgreich exportiert!
              </div>
            )}
          </div>

          {/* Import */}
          <div className="space-y-2 sm:space-y-3">
            <button
              onClick={() => setShowImportData(!showImportData)}
              className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-4 sm:px-6 py-3 rounded-lg font-medium transition-all duration-300 transform hover:scale-105 shadow-lg text-sm sm:text-base min-h-[44px] touch-manipulation"
            >
              📥 Spielstand importieren
            </button>
            
            {showImportData && (
              <div className="space-y-3 p-3 sm:p-4 bg-gray-50 rounded-lg">
                <textarea
                  value={importData}
                  onChange={(e) => setImportData(e.target.value)}
                  placeholder="Füge hier deine Spielstand-Daten ein..."
                  className="w-full h-24 sm:h-32 p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                />
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <button
                    onClick={importGameData}
                    className="w-full sm:flex-1 bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-lg font-medium transition-colors text-sm sm:text-base min-h-[44px] touch-manipulation"
                  >
                    Importieren
                  </button>
                  <button
                    onClick={() => {
                      setShowImportData(false);
                      setImportData('');
                    }}
                    className="w-full sm:flex-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-3 rounded-lg font-medium transition-colors text-sm sm:text-base min-h-[44px] touch-manipulation"
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Gefährliche Aktionen */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white rounded-xl p-4 sm:p-6 border border-red-200 shadow-lg"
      >
        <h2 className="text-lg sm:text-xl font-bold text-red-600 mb-3 sm:mb-4 flex items-center">
          ⚠️ Gefährliche Aktionen
        </h2>
        
        <div className="space-y-3 sm:space-y-4">
          <div className="p-3 sm:p-4 bg-red-50 rounded-lg border border-red-200">
            <div className="text-red-700 mb-3">
              <div className="font-medium text-sm sm:text-base">Spiel komplett zurücksetzen</div>
              <div className="text-xs sm:text-sm text-red-600 mt-1">
                ⚠️ Diese Aktion kann nicht rückgängig gemacht werden! Alle Fortschritte gehen verloren.
              </div>
            </div>
            
            {!showResetConfirm ? (
              <button
                onClick={() => setShowResetConfirm(true)}
                className="w-full sm:w-auto bg-red-500 hover:bg-red-600 text-white px-4 sm:px-6 py-3 rounded-lg font-medium transition-colors text-sm sm:text-base min-h-[44px] touch-manipulation"
              >
                🗑️ Spiel zurücksetzen
              </button>
            ) : (
              <div className="space-y-3">
                <div className="text-red-700 font-medium text-sm sm:text-base">
                  Bist du dir sicher? Diese Aktion kann nicht rückgängig gemacht werden!
                </div>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <button
                    onClick={handleResetGame}
                    className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-lg font-medium transition-colors text-sm sm:text-base min-h-[44px] touch-manipulation"
                  >
                    Ja, zurücksetzen
                  </button>
                  <button
                    onClick={() => setShowResetConfirm(false)}
                    className="w-full sm:w-auto bg-gray-500 hover:bg-gray-600 text-white px-4 py-3 rounded-lg font-medium transition-colors text-sm sm:text-base min-h-[44px] touch-manipulation"
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200"
      >
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          ℹ️ Spiel-Information
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="font-medium text-gray-700">Version</div>
            <div className="text-gray-600">VibeGarden v0.4.1</div>
          </div>
          <div>
            <div className="font-medium text-gray-700">Entwickelt mit</div>
            <div className="text-gray-600">React + TypeScript</div>
          </div>
          <div>
            <div className="font-medium text-gray-700">Speicher-Format</div>
            <div className="text-gray-600">LocalStorage (Browser)</div>
          </div>
          <div>
            <div className="font-medium text-gray-700">Kompatibilität</div>
            <div className="text-gray-600">Alle modernen Browser</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

function App() {
  const { matchThreeGame, achievements, addCoins, addGems, getTotalCoinsPerSecond, powerUps, upgrades, prestige, companions, incrementClick, coins, gems, formatNumber, updateCompanionActivities, canUseFreeCompanionDraw, useFreeCompanionDraw, canUseFreePremiumSpin, updateCooldowns, updatePremiumAutoClicker, offlineIncome, offlineGems, offlineCompanion, clearOfflineIncome, videoRewards, updateVideoRewardTime, updatePlayerActivity, checkAndUnlockDolfjinmann, checkAndUnlockSteve, checkAndUpgradeSteve, updateMining, updateContinuousMining, updateArmorCrafting, updateWeaponCrafting } = useGameStore();
  
  // Globale Speicher-Überwachung aktivieren
  useStorageManager();
  
  // Backup-System wird automatisch beim Import initialisiert
  // (gameBackupManager startet automatisch bei der ersten Verwendung)
  
  // Prüfe ob Begleiter in der Kaserne trainiert haben und Offline-Einkommen vorhanden ist
  const hasOfflineTraining = companions.training.length > 0 && offlineIncome > 0;
  const [currentTab, setCurrentTab] = useState<'clicker' | 'garden' | 'shop' | 'powerups' | 'prestige' | 'companions' | 'premium'>('clicker');
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showMatchThree, setShowMatchThree] = useState(false);
  const [matchThreeRingActive, setMatchThreeRingActive] = useState(false);
  const [showFreePremiumSpin, setShowFreePremiumSpin] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [clickPulseActive, setClickPulseActive] = useState(false);
  const handleFreePremiumSpinComplete = useCallback(() => {
    setShowFreePremiumSpin(false);
  }, []);
  const [showStatsOverlay, setShowStatsOverlay] = useState(false);
  // State für Edelstein-Video-Modal zentral verwalten
  const [showGemVideo, setShowGemVideo] = useState(false);
  const [gemRewarded, setGemRewarded] = useState(false);
  const [gemVideoCooldown, setGemVideoCooldown] = useState<number | null>(null);

  // Prüfe Cooldown beim Laden
  useEffect(() => {
    const savedCooldown = localStorage.getItem('gemVideoCooldown');
    if (savedCooldown) {
      const cooldownTime = parseInt(savedCooldown);
      const now = Date.now();
      if (now < cooldownTime) {
        setGemVideoCooldown(cooldownTime);
      } else {
        localStorage.removeItem('gemVideoCooldown');
      }
    }
  }, []);

  // Spieler-Aktivität alle 20 Sekunden aktualisieren
  useEffect(() => {
    const activityInterval = setInterval(() => {
      updatePlayerActivity();
    }, 20000); // Alle 20 Sekunden

    return () => clearInterval(activityInterval);
  }, [updatePlayerActivity]);

  // Prüfe und freischalten von Dolfjinmann beim Laden der App
  useEffect(() => {
    checkAndUnlockDolfjinmann();
  }, [checkAndUnlockDolfjinmann]);

  // Prüfe und freischalten von Steve beim Laden der App
  useEffect(() => {
    checkAndUnlockSteve();
  }, [checkAndUnlockSteve]);

  // Prüfe und aktualisiere Steve zu "Steve der Allmächtige"
  useEffect(() => {
    checkAndUpgradeSteve();
  }, [checkAndUpgradeSteve]);

  const handleGemClick = () => {
    const now = Date.now();
    if (gemVideoCooldown && now < gemVideoCooldown) {
      // Cooldown noch aktiv
      return;
    }
    updatePlayerActivity(); // Aktualisiere Aktivität bei Klick
    setShowGemVideo(true);
  };

  const handleGemVideoComplete = () => {
    if (!gemRewarded) {
      // Neue Logik: Mindestens 500 Edelsteine, bei mehr als 5000 Diamanten: 2x der aktuellen Diamanten
      let gemReward = 500; // Mindestbelohnung
      
      if (gems > 5000) {
        gemReward = Math.floor(gems * 2); // 2x der aktuellen Diamanten
      }
      
      addGems(gemReward);
      setGemRewarded(true);
      
      // Setze 5-Minuten-Cooldown
      const cooldownEnd = Date.now() + (5 * 60 * 1000); // 5 Minuten
      setGemVideoCooldown(cooldownEnd);
      localStorage.setItem('gemVideoCooldown', cooldownEnd.toString());
    }
    updatePlayerActivity(); // Aktualisiere Aktivität nach Video
    setTimeout(() => {
      setShowGemVideo(false);
      setGemRewarded(false);
    }, 1200);
  };

  // Cooldown-Timer
  useEffect(() => {
    if (gemVideoCooldown) {
      const interval = setInterval(() => {
        const now = Date.now();
        if (now >= gemVideoCooldown) {
          setGemVideoCooldown(null);
          localStorage.removeItem('gemVideoCooldown');
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [gemVideoCooldown]);

  // Hilfsfunktion für Cooldown-Anzeige
  const getCooldownTime = () => {
    if (!gemVideoCooldown) return null;
    const now = Date.now();
    const remaining = Math.max(0, gemVideoCooldown - now);
    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const isGemVideoAvailable = !gemVideoCooldown || Date.now() >= gemVideoCooldown;

  // Berechne die Edelstein-Belohnung für Video-Werbung
  const calculateGemReward = () => {
    let gemReward = 500; // Mindestbelohnung
    
    if (gems > 5000) {
      gemReward = Math.floor(gems * 2); // 2x der aktuellen Diamanten
    }
    
    return gemReward;
  };

  // Auto-income every second (faster with Speed Boost)
  useEffect(() => {
    const intervalTime = powerUps?.speedBoost?.isActive ? 500 : 1000;
    const interval = setInterval(() => {
      const coinsPerSecond = getTotalCoinsPerSecond();
      if (coinsPerSecond > 0) {
        // Angepasste Rate für Speed Boost
        const adjustedAmount = powerUps?.speedBoost?.isActive ? coinsPerSecond / 2 : coinsPerSecond;
        const goldRushMultiplier = powerUps?.goldRush?.isActive ? 2 : 1;
        const finalAmount = Math.floor(adjustedAmount * goldRushMultiplier);
        addCoins(finalAmount);
      }
    }, intervalTime);

    return () => clearInterval(interval);
  }, [addCoins, getTotalCoinsPerSecond, powerUps?.speedBoost?.isActive, powerUps?.goldRush?.isActive]);

  // Crystal Generator - erzeugt Edelsteine über Zeit
  useEffect(() => {
    if ((upgrades?.crystalGenerator || 0) > 0) {
      const interval = setInterval(() => {
        const gemsPerMinute = (upgrades?.crystalGenerator || 0) * 0.1; // 0.1 Edelsteine pro Level pro Minute
        if (Math.random() < gemsPerMinute / 60) { // Pro Sekunde Chance
          addGems(1);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [addGems, upgrades?.crystalGenerator]);

  // Match-Three Ring Animation - alle 5 Minuten
  useEffect(() => {
    if (matchThreeGame?.isUnlocked) {
      const interval = setInterval(() => {
        setMatchThreeRingActive(true);
        setTimeout(() => setMatchThreeRingActive(false), 3000); // Ring bleibt 3 Sekunden aktiv
      }, 5 * 60 * 1000); // 5 Minuten

      // Initialer Ring nach 30 Sekunden
      const initialTimeout = setTimeout(() => {
        setMatchThreeRingActive(true);
        setTimeout(() => setMatchThreeRingActive(false), 3000);
      }, 30000);

      return () => {
        clearInterval(interval);
        clearTimeout(initialTimeout);
      };
    }
  }, [matchThreeGame?.isUnlocked]);

  // Begleiter-Aktivitäten (Training & Reisen) - alle 10 Sekunden prüfen
  useEffect(() => {
    const interval = setInterval(() => {
      updateCompanionActivities();
    }, 10000); // 10 Sekunden

    return () => clearInterval(interval);
  }, [updateCompanionActivities]);

  // Kontinuierliche Mining-Aktivitäten - jede Sekunde
  useEffect(() => {
    const interval = setInterval(() => {
      updateMining();
      updateContinuousMining();
      updateArmorCrafting();
      updateWeaponCrafting();
    }, 1000); // Jede Sekunde

    return () => clearInterval(interval);
  }, [updateMining, updateContinuousMining, updateArmorCrafting, updateWeaponCrafting]);

  // Cooldown-Updates und Timer-Update für kostenlose Buttons
  const [, setTimerUpdate] = useState(0);
  useEffect(() => {
    // Initiale Cooldown-Überprüfung beim App-Start
    updateCooldowns();
    
    const interval = setInterval(() => {
      updateCooldowns(); // Überprüfe Cooldowns
      setTimerUpdate(prev => prev + 1); // Trigger re-render für Timer-Update
    }, 60000); // 1 Minute

    return () => clearInterval(interval);
  }, [updateCooldowns]);

  // Premium Auto-Klicker - läuft unabhängig vom aktuellen Tab
  useEffect(() => {
    const interval = setInterval(() => {
      updatePremiumAutoClicker();
    }, 100); // Alle 100ms für präzise Kontrolle

    return () => clearInterval(interval);
  }, [updatePremiumAutoClicker]);

  // Training-System - läuft global jede Sekunde
  const { updateTraining } = useGameStore();
  useEffect(() => {
    const interval = setInterval(() => {
      updateTraining();
    }, 1000); // Jede Sekunde

    return () => clearInterval(interval);
  }, [updateTraining]);

  // Video-Reward-Zeit Update - läuft jede Sekunde
  useEffect(() => {
    const interval = setInterval(() => {
      updateVideoRewardTime();
    }, 1000); // Jede Sekunde

    return () => clearInterval(interval);
  }, [updateVideoRewardTime]);

  // Mobile Sticky Stats - immer sichtbar auf Mobile
  useEffect(() => {
    const stickyElement = document.getElementById('mobile-sticky-stats');
    const mainContent = document.getElementById('main-content');
    
    if (stickyElement && mainContent && window.innerWidth < 640) { // Nur auf Mobile
      // Sticky Anzeige immer sichtbar halten
      stickyElement.style.transform = 'translateY(0)';
      stickyElement.style.height = 'auto';
      stickyElement.style.overflow = 'visible';
      stickyElement.style.opacity = '1';
      // Main Content direkt an die sticky Anzeige andocken
      mainContent.style.marginTop = '0';
      mainContent.style.paddingTop = '0';
    }
  }, []);

  // Platzhalter für Booster-Pack-Funktionen:
  const addBoosterPackToInventory = (type: string, amount: number) => {
    // TODO: Implementiere die echte Funktion im GameStore
    if (window && window.console) {
      console.log(`Booster Pack vergeben: ${amount}x ${type}`);
    }
  };

  useEffect(() => {
    if (localStorage.getItem('update-0.4.2-reward')) {
      console.log('Update 0.4.2 Reward wird vergeben...');
      
      try {
        // 1 Mio Diamanten
        console.log('Vergebe 1.000.000 Diamanten...');
        addGems(1000000);
        console.log('✅ 1.000.000 Diamanten vergeben');

        // Prüfe verfügbare GameStore-Funktionen
        const gameStore = useGameStore.getState();
        console.log('Verfügbare GameStore-Funktionen:', Object.keys(gameStore));

        // Hilfsfunktionen für Booster-Pack-Generierung (aus PremiumShop.tsx)
        const generateRandomCompanion = (type: 'legendary' | 'epic' | 'rare' | 'common') => {
          const companionNames = {
            legendary: [
              'Göttlicher Phoenix', 'Kosmischer Drache', 'Himmlischer Wächter', 'Ewiger Wächter', 'Göttlicher Krieger',
              'Universeller Meister', 'Omnipotenter Herrscher', 'Kosmischer Titan', 'Allmächtiger Sage', 'Göttlicher Kaiser',
              'Ewiger Beschützer', 'Himmlischer Bote', 'Kosmischer Wanderer', 'Göttlicher Helfer', 'Universeller Wächter',
              'Allmächtiger Krieger', 'Kosmischer Meister', 'Göttlicher Titan', 'Ewiger Kaiser', 'Himmlischer Herrscher',
              'Omnipotenter Wächter', 'Universeller Titan', 'Kosmischer Kaiser', 'Göttlicher Wanderer', 'Allmächtiger Bote',
              'Ewiger Meister', 'Himmlischer Titan', 'Kosmischer Herrscher', 'Göttlicher Kaiser', 'Universeller Bote'
            ],
            epic: [
              'Epischer Krieger', 'Mystischer Zauberer', 'Kosmischer Wanderer', 'Ewiger Beschützer', 'Göttlicher Helfer',
              'Arkaner Meister', 'Elementar-Geist', 'Himmlischer Bote', 'Magischer Beschützer', 'Uralter Weiser',
              'Kosmischer Wanderer', 'Epischer Zauberer', 'Mystischer Krieger', 'Göttlicher Beschützer', 'Ewiger Helfer',
              'Arkaner Geist', 'Elementar-Meister', 'Himmlischer Weiser', 'Magischer Bote', 'Uralter Krieger',
              'Kosmischer Zauberer', 'Epischer Geist', 'Mystischer Meister', 'Göttlicher Weiser', 'Ewiger Bote',
              'Arkaner Krieger', 'Elementar-Zauberer', 'Himmlischer Geist', 'Magischer Meister', 'Uralter Beschützer'
            ],
            rare: [
              'Seltsamer Begleiter', 'Mystischer Freund', 'Kosmischer Helfer', 'Ewiger Begleiter', 'Göttlicher Freund',
              'Kristall-Kätzchen', 'Silber-Wolf', 'Gold-Fuchs', 'Saphir-Eule', 'Rubin-Drache',
              'Smaragd-Tiger', 'Perlen-Delfin', 'Opal-Schmetterling', 'Seltsamer Helfer', 'Mystischer Begleiter',
              'Kosmischer Freund', 'Ewiger Helfer', 'Göttlicher Begleiter', 'Kristall-Freund', 'Silber-Helfer',
              'Gold-Begleiter', 'Saphir-Freund', 'Rubin-Helfer', 'Smaragd-Begleiter', 'Perlen-Freund',
              'Opal-Helfer', 'Seltsamer Freund', 'Mystischer Helfer', 'Kosmischer Begleiter', 'Ewiger Freund'
            ],
            common: [
              'Treuer Begleiter', 'Loyaler Freund', 'Vertrauter Helfer', 'Ewiger Freund', 'Göttlicher Helfer',
              'Felix', 'Luna', 'Max', 'Bella', 'Charlie', 'Daisy', 'Rocky', 'Mia', 'Buddy', 'Lily',
              'Treuer Freund', 'Loyaler Helfer', 'Vertrauter Begleiter', 'Ewiger Helfer', 'Göttlicher Freund',
              'Felix', 'Luna', 'Max', 'Bella', 'Charlie', 'Daisy', 'Rocky', 'Mia', 'Buddy', 'Lily',
              'Treuer Helfer', 'Loyaler Begleiter', 'Vertrauter Freund', 'Ewiger Begleiter', 'Göttlicher Helfer'
            ]
          };
          
          const companionIcons = {
            legendary: [
              '🔥', '🐉', '👼', '⚡', '🌟', '👑', '🌌', '⚡', '🔱', '🌟',
              '💫', '🌌', '⚡', '🔱', '🌟', '💫', '🌌', '⚡', '🔱', '🌟',
              '💫', '🌌', '⚡', '🔱', '🌟', '💫', '🌌', '⚡', '🔱', '🌟'
            ],
            epic: [
              '⚔️', '🔮', '🌌', '🛡️', '👑', '🧙‍♂️', '🔮', '⚡', '🌟', '🔥',
              '❄️', '🌙', '☀️', '⚔️', '🔮', '🌌', '🛡️', '👑', '🧙‍♂️', '🔮',
              '⚡', '🌟', '🔥', '❄️', '🌙', '☀️', '⚔️', '🔮', '🌌', '🛡️'
            ],
            rare: [
              '🐾', '✨', '💫', '🌙', '⭐', '💎', '🦊', '🦉', '🐅', '🐬',
              '🦋', '🦄', '🐉', '🐾', '✨', '💫', '🌙', '⭐', '💎', '🦊',
              '🦉', '🐅', '🐬', '🦋', '🦄', '🐉', '🐾', '✨', '💫', '🌙'
            ],
            common: [
              '🐕', '🐱', '🐦', '🐰', '🐸', '🐱', '🐶', '🐹', '🐰', '🐸',
              '🐤', '🐧', '🦔', '🐨', '🐼', '🐕', '🐱', '🐦', '🐰', '🐸',
              '🐱', '🐶', '🐹', '🐰', '🐸', '🐤', '🐧', '🦔', '🐨', '🐼'
            ]
          };
          
          const name = companionNames[type][Math.floor(Math.random() * companionNames[type].length)];
          const icon = companionIcons[type][Math.floor(Math.random() * companionIcons[type].length)];
          
          // Generiere Fähigkeiten basierend auf Seltenheit
          const abilityCount = type === 'legendary' ? 5 : type === 'epic' ? 4 : type === 'rare' ? 3 : 2;
          const abilities = [];
          
          const abilityTypes = [
            { type: 'clickMultiplier', description: 'Münzen pro Klick' },
            { type: 'coinPerSecond', description: 'Münzen pro Sekunde' },
            { type: 'gemChance', description: 'Edelstein-Chance' },
            { type: 'xpBonus', description: 'Erfahrung' },
            { type: 'gardenBonus', description: 'Garten-Boni' },
            { type: 'autoClickPower', description: 'Auto-Klick-Power' },
            { type: 'upgradeDiscount', description: 'Upgrade-Rabatt' },
            { type: 'prestigeBonus', description: 'Prestige-Punkte' }
          ];
          
          const usedTypes = new Set();
          
          for (let i = 0; i < abilityCount; i++) {
            let abilityType;
            do {
              abilityType = abilityTypes[Math.floor(Math.random() * abilityTypes.length)];
            } while (usedTypes.has(abilityType.type));
            
            usedTypes.add(abilityType.type);
            
            // Werte basierend auf Seltenheit
            let value;
            switch (type) {
              case 'legendary':
                value = Math.floor(Math.random() * 1000) + 500; // 500-1500
                break;
              case 'epic':
                value = Math.floor(Math.random() * 300) + 200; // 200-500
                break;
              case 'rare':
                value = Math.floor(Math.random() * 100) + 50; // 50-150
                break;
              default:
                value = Math.floor(Math.random() * 50) + 10; // 10-60
            }
            
            abilities.push({
              type: abilityType.type,
              value: value,
              description: `+${value}% ${abilityType.description}`
            });
          }
          
          return {
            id: `booster_${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            name: name,
            type: type,
            icon: icon,
            description: `Booster Pack Begleiter: ${name}`,
            abilities: abilities,
            discoveredAt: new Date().toISOString()
          };
        };

        const generateBoosterPack = (packConfig: any) => {
          const companions: any[] = [];
          
          // Generiere garantierte legendäre Begleiter
          for (let i = 0; i < packConfig.guaranteedLegendary; i++) {
            companions.push(generateRandomCompanion('legendary'));
          }
          
          // Generiere garantierte epische Begleiter
          for (let i = 0; i < packConfig.guaranteedEpic; i++) {
            companions.push(generateRandomCompanion('epic'));
          }
          
          // Generiere garantierte seltene Begleiter
          for (let i = 0; i < packConfig.guaranteedRare; i++) {
            companions.push(generateRandomCompanion('rare'));
          }
          
          // Generiere zufällige Begleiter
          for (let i = 0; i < packConfig.random; i++) {
            const rarity = Math.random();
            let type: 'legendary' | 'epic' | 'rare' | 'common';
            
            if (rarity < 0.05) type = 'legendary';
            else if (rarity < 0.15) type = 'epic';
            else if (rarity < 0.35) type = 'rare';
            else type = 'common';
            
            companions.push(generateRandomCompanion(type));
          }
          
          return companions;
        };

        // --- ULTIMATIVES PAKET (wie Ultimate Bundle) ---
        console.log('Vergebe Ultimatives Paket...');
        if (gameStore.addCoins) {
          gameStore.addCoins(10000000);
          console.log('✅ +10.000.000 Münzen vergeben');
        }
        if (gameStore.addGems) {
          gameStore.addGems(10000);
          console.log('✅ +10.000 Edelsteine vergeben');
        }
        if (gameStore.purchaseUpgradeBulk) {
          gameStore.purchaseUpgradeBulk('shovelLevel', 5);
          gameStore.purchaseUpgradeBulk('autoDiggerLevel', 5);
          gameStore.purchaseUpgradeBulk('fertilizer', 5);
          gameStore.purchaseUpgradeBulk('wateringCan', 5);
          console.log('✅ Alle Upgrades +5 vergeben');
        }
        // Ultimate Bundle Begleiter (3 legendäre)
        const ultimateCompanions = generateBoosterPack({
          packSize: 7,
          guaranteedLegendary: 3,
          guaranteedEpic: 2,
          guaranteedRare: 1,
          random: 1
        });
        ultimateCompanions.forEach(companion => {
          if (gameStore.addCompanionToCollection) {
            gameStore.addCompanionToCollection(companion);
            console.log(`✅ Ultimate Bundle Begleiter: ${companion.name} (${companion.type})`);
          }
        });

        // --- DIVINE BOOSTER PACK ---
        console.log('Vergebe Divine Booster Pack...');
        if (gameStore.addCoins) {
          gameStore.addCoins(1000000);
          console.log('✅ +1.000.000 Münzen vergeben');
        }
        if (gameStore.addGems) {
          gameStore.addGems(1000);
          console.log('✅ +1.000 Edelsteine vergeben');
        }
        // Divine Booster Pack (3 legendäre)
        const divineCompanions = generateBoosterPack({
          packSize: 7,
          guaranteedLegendary: 3,
          guaranteedEpic: 2,
          guaranteedRare: 1,
          random: 1
        });
        divineCompanions.forEach(companion => {
          if (gameStore.addCompanionToCollection) {
            gameStore.addCompanionToCollection(companion);
            console.log(`✅ Divine Booster Begleiter: ${companion.name} (${companion.type})`);
          }
        });

        // --- LEGENDARY BOOSTER PACK ---
        console.log('Vergebe Legendary Booster Pack...');
        if (gameStore.addCoins) {
          gameStore.addCoins(500000);
          console.log('✅ +500.000 Münzen vergeben');
        }
        if (gameStore.addGems) {
          gameStore.addGems(500);
          console.log('✅ +500 Edelsteine vergeben');
        }
        // Legendary Booster Pack (2 legendäre)
        const legendaryCompanions = generateBoosterPack({
          packSize: 7,
          guaranteedLegendary: 2,
          guaranteedEpic: 3,
          guaranteedRare: 1,
          random: 1
        });
        legendaryCompanions.forEach(companion => {
          if (gameStore.addCompanionToCollection) {
            gameStore.addCompanionToCollection(companion);
            console.log(`✅ Legendary Booster Begleiter: ${companion.name} (${companion.type})`);
          }
        });

        // --- 3x EPIC BOOSTER PACKS ---
        console.log('Vergebe 3x Epic Booster Packs...');
        for (let i = 0; i < 3; i++) {
          if (gameStore.addCoins) {
            gameStore.addCoins(100000);
            console.log(`✅ Epic Booster ${i+1}: +100.000 Münzen vergeben`);
          }
          if (gameStore.addGems) {
            gameStore.addGems(100);
            console.log(`✅ Epic Booster ${i+1}: +100 Edelsteine vergeben`);
          }
          // Epic Booster Pack (1 legendärer, 3 epische)
          const epicCompanions = generateBoosterPack({
            packSize: 7,
            guaranteedLegendary: 1,
            guaranteedEpic: 3,
            guaranteedRare: 2,
            random: 1
          });
          epicCompanions.forEach(companion => {
            if (gameStore.addCompanionToCollection) {
              gameStore.addCompanionToCollection(companion);
              console.log(`✅ Epic Booster ${i+1} Begleiter: ${companion.name} (${companion.type})`);
            }
          });
        }

        console.log('🎉 Alle Update 0.4.2 Belohnungen erfolgreich vergeben!');
        
        // Flag entfernen
        localStorage.removeItem('update-0.4.2-reward');
        
      } catch (error) {
        console.error('❌ Fehler beim Vergeben der Update-Belohnungen:', error);
        alert('Es gab einen Fehler beim Vergeben der Belohnungen. Bitte schaue in die Konsole für Details.');
      }
    }
  }, [addGems]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex flex-col">

      {/* Update 0.4.2 Banner - nur für Spieler mit Begleitern vor 3.8.2025 */}
      {localStorage.getItem('garden-clicker-storage') && 
       !localStorage.getItem('update-0.4.2-seen') && 
       (() => {
         // Prüfe ob der Spieler Begleiter hat, die vor dem 3.8.2025 entdeckt wurden
         const gameStore = useGameStore.getState();
         const companions = gameStore.companions?.discovered || [];
         const cutoffDate = new Date('2025-08-03T00:00:00.000Z');
         
         const hasOldCompanions = companions.some((companion: any) => {
           if (companion.discoveredAt) {
             const discoveredDate = new Date(companion.discoveredAt);
             return discoveredDate < cutoffDate;
           }
           return false;
         });
         
         return hasOldCompanions;
       })() && (
        <div className="fixed top-24 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md px-4">
          <motion.div
            initial={{ opacity: 0, y: -50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -50, scale: 0.9 }}
            className="bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 rounded-2xl p-6 shadow-2xl border-2 border-blue-400 relative overflow-hidden"
          >
            {/* Sparkle Effect */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute top-4 left-4 w-2 h-2 bg-yellow-300 rounded-full animate-ping"></div>
              <div className="absolute top-8 right-8 w-1 h-1 bg-white rounded-full animate-pulse"></div>
              <div className="absolute bottom-6 left-1/2 w-1.5 h-1.5 bg-yellow-200 rounded-full animate-bounce"></div>
            </div>

            <div className="relative z-10 max-h-[70vh] overflow-y-auto">
              <div className="text-center mb-4">
                <h3 className="text-2xl font-bold text-white mb-2 flex items-center justify-center">
                  <span className="mr-2">🎉</span>
                  Update 0.4.2
                  <span className="ml-2">🎉</span>
                </h3>
                <p className="text-yellow-200 font-semibold text-lg">Neue Börse verfügbar!</p>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-yellow-200 mb-2">🎯 Neue Features:</h4>
                  <ul className="space-y-1 text-blue-100">
                    <li>• 📈 Vollständige Börse mit 50+ Aktien</li>
                    <li>• 💰 Trading mit Münzen und Diamanten</li>
                    <li>• 🤖 1000 virtuelle Spieler</li>
                    <li>• 📊 Live-Charts und Marktdaten</li>
                    <li>• 📰 News-System mit Marktbeeinflussung</li>
                    <li>• 🎯 Portfolio-Tracking und Performance</li>
                  </ul>
                </div>

                <div className="bg-blue-800 bg-opacity-50 rounded-lg p-4 border border-blue-400">
                  <h4 className="font-semibold text-yellow-200 mb-2">⚠️ Wichtiger Hinweis:</h4>
                  <p className="text-blue-100 text-sm leading-relaxed">
                    Für das beste Erlebnis mit der neuen Börse empfehlen wir einen neuen Spielstand. 
                    Du erhältst dafür ein großes Belohnungspaket mit ultimativen Begleitern und Ressourcen!
                  </p>
                </div>

                <div className="flex justify-center">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      localStorage.setItem('show-rewards-banner', 'true');
                    }}
                    className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white font-bold py-3 px-6 rounded-xl shadow-lg transform transition-all duration-300"
                  >
                    🎁 Neuer Spielstand & Belohnung
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Belohnungen Banner - wird angezeigt nach Klick auf "Neuer Spielstand & Belohnung" */}
      {localStorage.getItem('show-rewards-banner') && (
        <div className="fixed top-24 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md px-4">
          <motion.div
            initial={{ opacity: 0, y: -50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -50, scale: 0.9 }}
            className="bg-gradient-to-br from-purple-600 via-pink-600 to-yellow-500 rounded-2xl p-6 shadow-2xl border-2 border-purple-400 relative overflow-hidden"
          >
            {/* Animated Background Pattern */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-purple-500/20 rounded-2xl"></div>
            
            {/* Floating Sparkles */}
            <motion.div 
              animate={{ 
                y: [-3, 3, -3],
                rotate: [0, 180, 360]
              }}
              transition={{ duration: 2.5, repeat: Infinity }}
              className="absolute top-2 left-3 text-yellow-200 text-sm"
            >
              ⭐
            </motion.div>
            <motion.div 
              animate={{ 
                y: [3, -3, 3],
                scale: [1, 1.1, 1]
              }}
              transition={{ duration: 2, repeat: Infinity, delay: 0.3 }}
              className="absolute top-4 right-6 text-yellow-200 text-xs"
            >
              💎
            </motion.div>
            <motion.div 
              animate={{ 
                y: [-2, 2, -2],
                x: [0, 2, 0]
              }}
              transition={{ duration: 1.8, repeat: Infinity, delay: 0.6 }}
              className="absolute bottom-4 left-6 text-yellow-200 text-xs"
            >
              🎁
            </motion.div>

            <div className="relative z-10 max-h-[70vh] overflow-y-auto">
              <div className="text-center mb-6">
                <motion.div
                  animate={{ 
                    scale: [1, 1.05, 1],
                    rotate: [0, 3, -3, 0]
                  }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="text-4xl mb-3 filter drop-shadow-lg"
                >
                  🎁
                </motion.div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  Deine Belohnungen
                </h3>
                <p className="text-yellow-200 font-semibold text-lg">Für das Update 0.4.2!</p>
                <div className="w-20 h-1 bg-gradient-to-r from-yellow-400 to-orange-500 mx-auto mt-3 rounded-full"></div>
              </div>

              <div className="space-y-4">
                {/* Ultimatives Paket */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1 }}
                  className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-xl p-4 border border-yellow-400/30"
                >
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-2">🏆</span>
                    <h4 className="font-bold text-yellow-200 text-lg">Ultimatives Paket</h4>
                  </div>
                  <ul className="space-y-1 text-yellow-100 text-sm">
                    <li className="flex items-center">
                      <span className="mr-2">💰</span>
                      <span>+10.000.000 Münzen</span>
                    </li>
                    <li className="flex items-center">
                      <span className="mr-2">💎</span>
                      <span>+10.000 Edelsteine</span>
                    </li>
                    <li className="flex items-center">
                      <span className="mr-2">🔧</span>
                      <span>Alle Upgrades +5</span>
                    </li>
                    <li className="flex items-center">
                      <span className="mr-2">👥</span>
                      <span>7 Begleiter (3 legendär, 2 episch, 1 selten, 1 zufällig)</span>
                    </li>
                  </ul>
                </motion.div>

                {/* Divine Booster Pack */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl p-4 border border-purple-400/30"
                >
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-2">✨</span>
                    <h4 className="font-bold text-purple-200 text-lg">Divine Booster Pack</h4>
                  </div>
                  <ul className="space-y-1 text-purple-100 text-sm">
                    <li className="flex items-center">
                      <span className="mr-2">💰</span>
                      <span>+1.000.000 Münzen</span>
                    </li>
                    <li className="flex items-center">
                      <span className="mr-2">💎</span>
                      <span>+1.000 Edelsteine</span>
                    </li>
                    <li className="flex items-center">
                      <span className="mr-2">👥</span>
                      <span>7 Begleiter (3 legendär, 2 episch, 1 selten, 1 zufällig)</span>
                    </li>
                  </ul>
                </motion.div>

                {/* Legendary Booster Pack */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-xl p-4 border border-blue-400/30"
                >
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-2">⭐</span>
                    <h4 className="font-bold text-blue-200 text-lg">Legendary Booster Pack</h4>
                  </div>
                  <ul className="space-y-1 text-blue-100 text-sm">
                    <li className="flex items-center">
                      <span className="mr-2">💰</span>
                      <span>+500.000 Münzen</span>
                    </li>
                    <li className="flex items-center">
                      <span className="mr-2">💎</span>
                      <span>+500 Edelsteine</span>
                    </li>
                    <li className="flex items-center">
                      <span className="mr-2">👥</span>
                      <span>7 Begleiter (2 legendär, 3 episch, 1 selten, 1 zufällig)</span>
                    </li>
                  </ul>
                </motion.div>

                {/* 3x Epic Booster Packs */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl p-4 border border-green-400/30"
                >
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-2">💎</span>
                    <h4 className="font-bold text-green-200 text-lg">3x Epic Booster Packs</h4>
                  </div>
                  <ul className="space-y-1 text-green-100 text-sm">
                    <li className="flex items-center">
                      <span className="mr-2">💰</span>
                      <span>+300.000 Münzen (3x 100.000)</span>
                    </li>
                    <li className="flex items-center">
                      <span className="mr-2">💎</span>
                      <span>+300 Edelsteine (3x 100)</span>
                    </li>
                    <li className="flex items-center">
                      <span className="mr-2">👥</span>
                      <span>21 Begleiter (3x 7: je 1 legendär, 3 episch, 2 selten, 1 zufällig)</span>
                    </li>
                  </ul>
                </motion.div>

                {/* Bonus */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="bg-gradient-to-r from-red-500/20 to-pink-500/20 rounded-xl p-4 border border-red-400/30"
                >
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-2">💰</span>
                    <h4 className="font-bold text-red-200 text-lg">Bonus</h4>
                  </div>
                  <p className="text-red-100 text-sm font-semibold">
                    <span className="text-yellow-300">+1.000.000 Diamanten</span> aus dem Premium-Shop!
                  </p>
                </motion.div>

                {/* Gesamtsumme */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6 }}
                  className="bg-gradient-to-r from-indigo-500/30 to-purple-500/30 rounded-xl p-4 border border-indigo-400/30 text-center"
                >
                  <h4 className="font-bold text-indigo-200 text-lg mb-2">🎯 Gesamtsumme</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="text-indigo-100">
                      <span className="font-semibold">💰 12.800.000 Münzen</span>
                    </div>
                    <div className="text-indigo-100">
                      <span className="font-semibold">💎 1.012.800 Edelsteine</span>
                    </div>
                    <div className="text-indigo-100">
                      <span className="font-semibold">👥 42 Begleiter</span>
                    </div>
                    <div className="text-indigo-100">
                      <span className="font-semibold">🔧 Alle Upgrades +5</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  className="flex justify-center pt-2"
                >
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      localStorage.clear();
                      localStorage.setItem('update-0.4.2-seen', 'true');
                      localStorage.setItem('update-0.4.2-reward', 'true');
                      window.location.reload();
                    }}
                    className="bg-gradient-to-r from-green-400 via-blue-500 to-purple-600 hover:from-green-500 hover:via-blue-600 hover:to-purple-700 text-white font-bold py-4 px-8 rounded-xl shadow-lg transform transition-all duration-300 text-lg"
                  >
                    🚀 Jetzt neustarten
                  </motion.button>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Offline-Einkommen Anzeige */}
      {(offlineIncome > 0 || offlineGems > 0 || offlineCompanion) && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50">
          <motion.div
            initial={{ opacity: 0, y: -30, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ 
              type: "spring", 
              stiffness: 300, 
              damping: 20,
              duration: 0.6 
            }}
            className="relative"
          >
            {/* Glow Effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-purple-400 via-pink-500 to-orange-500 rounded-2xl blur-xl opacity-60 animate-pulse"></div>
            
            {/* Main Card */}
            <div className="relative bg-gradient-to-br from-purple-600 via-pink-600 to-orange-600 text-white px-6 py-6 rounded-2xl shadow-2xl border-2 border-purple-300/50 transform hover:scale-105 transition-all duration-300 backdrop-blur-sm">
              {/* Animated Background Pattern */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-purple-500/20 rounded-2xl"></div>
              
              {/* Floating Sparkles */}
              <motion.div 
                animate={{ 
                  y: [-3, 3, -3],
                  rotate: [0, 180, 360]
                }}
                transition={{ duration: 2.5, repeat: Infinity }}
                className="absolute top-2 left-3 text-yellow-200 text-sm"
              >
                ✨
              </motion.div>
              <motion.div 
                animate={{ 
                  y: [3, -3, 3],
                  scale: [1, 1.1, 1]
                }}
                transition={{ duration: 2, repeat: Infinity, delay: 0.3 }}
                className="absolute top-4 right-6 text-yellow-200 text-xs"
              >
                ⭐
              </motion.div>
              
              <div className="flex items-center gap-4 relative z-10">
                {/* Animated Icon */}
                <div className="relative">
                  <motion.div 
                    animate={{ 
                      scale: [1, 1.05, 1],
                      rotate: [0, 3, -3, 0]
                    }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="text-4xl filter drop-shadow-lg"
                  >
                    💰
                  </motion.div>
                  <motion.div 
                    animate={{ 
                      rotate: 360,
                      scale: [1, 1.1, 1]
                    }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="absolute -top-1 -right-1 text-lg text-yellow-300"
                  >
                    ⚡
                  </motion.div>
                </div>
                
                {/* Content */}
                <div className="text-center flex-1">
                  <motion.div 
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="font-bold text-xl mb-2 drop-shadow-lg bg-gradient-to-r from-white to-yellow-200 bg-clip-text text-transparent"
                  >
                    🎉 Willkommen zurück!
                  </motion.div>
                  
                  {/* Zeit-Anzeige */}
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="text-xs opacity-80 mb-2"
                  >
                    {(() => {
                      const now = Date.now();
                      const timeDiff = now - (useGameStore.getState().offlineEndTime || now);
                      const minutesDiff = Math.floor(timeDiff / (1000 * 60));
                      const hoursDiff = Math.floor(minutesDiff / 60);
                      const remainingMinutes = minutesDiff % 60;
                      
                      if (hoursDiff > 0) {
                        return `${hoursDiff}h ${remainingMinutes}m offline`;
                      } else {
                        return `${minutesDiff}m offline`;
                      }
                    })()}
                  </motion.div>
                  
                  {/* Rewards Container - Kompakter */}
                  <div className="space-y-1 mb-2">
                    {/* Münzen */}
                    {offlineIncome > 0 && (
                      <motion.div 
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.4 }}
                        className="bg-gradient-to-r from-yellow-500/80 to-orange-500/80 text-white px-3 py-1 rounded-full font-bold text-sm shadow-lg border border-yellow-300/50"
                      >
                        +{formatNumber(offlineIncome)} 💰
                      </motion.div>
                    )}
                    
                    {/* Diamanten */}
                    {offlineGems > 0 && (
                      <motion.div 
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.5 }}
                        className="bg-gradient-to-r from-blue-500/80 to-purple-500/80 text-white px-3 py-1 rounded-full font-bold text-sm shadow-lg border border-blue-300/50"
                      >
                        +{offlineGems} 💎
                      </motion.div>
                    )}
                    
                    {/* Begleiter */}
                    {offlineCompanion && (
                      <motion.div 
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.6 }}
                        className="bg-gradient-to-r from-green-500/80 to-emerald-500/80 text-white px-3 py-1 rounded-full font-bold text-sm shadow-lg border border-green-300/50"
                      >
                        {offlineCompanion.icon} {offlineCompanion.name}
                      </motion.div>
                    )}
                    
                    {/* Offline-Training */}
                    {hasOfflineTraining && (
                      <motion.div 
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.7 }}
                        className="bg-gradient-to-r from-blue-500/80 to-purple-500/80 text-white px-3 py-1 rounded-full font-bold text-sm shadow-lg border border-blue-300/50"
                      >
                        🏰 {companions.training.length} Begleiter trainiert
                      </motion.div>
                    )}
                  </div>
                  
                  {/* Kompakte Info */}
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8 }}
                    className="text-xs opacity-75 bg-black/20 px-2 py-1 rounded-full inline-block"
                  >
                    1.5x 💰 | 3x 💎 | 0.2% 🎭{hasOfflineTraining ? ' | 🏰 Training' : ''}
                  </motion.div>
                </div>
                
                {/* Close Button */}
                <motion.button
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.9 }}
                  onClick={clearOfflineIncome}
                  className="group relative p-2 hover:bg-white/20 rounded-full transition-all duration-200 hover:scale-110"
                >
                  <span className="text-lg group-hover:scale-110 transition-transform duration-200">✕</span>
                  <div className="absolute inset-0 bg-white/10 rounded-full scale-0 group-hover:scale-100 transition-transform duration-200"></div>
                </motion.button>
              </div>
              
              {/* Animated Border */}
              <motion.div 
                animate={{ 
                  background: [
                    "linear-gradient(90deg, transparent, white, transparent)",
                    "linear-gradient(180deg, transparent, white, transparent)",
                    "linear-gradient(270deg, transparent, white, transparent)",
                    "linear-gradient(0deg, transparent, white, transparent)"
                  ]
                }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute bottom-0 left-0 right-0 h-1 rounded-b-3xl"
              />
            </div>
          </motion.div>
        </div>
      )}

      {/* Tab Navigation mit XP-Leiste */}
      <div className="sticky top-0 z-50">
        <nav className="bg-gradient-to-r from-white via-blue-50 to-purple-50 border-b border-gray-200 shadow-lg backdrop-blur-md">
          <div className="w-full relative">
            {/* Decorative gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"></div>
            
            <div className="grid grid-cols-7 relative z-10">
              <button
                onClick={() => { setCurrentTab('clicker'); updatePlayerActivity(); }}
                className={`group px-1 sm:px-2 lg:px-3 py-2 sm:py-3 font-medium transition-all duration-300 border-b-3 text-xs sm:text-sm lg:text-base flex flex-col sm:flex-row items-center justify-center min-h-[48px] relative overflow-hidden ${
                  currentTab === 'clicker'
                    ? 'border-green-500 text-green-700 bg-gradient-to-b from-green-50 to-emerald-100 shadow-inner'
                    : 'border-transparent text-gray-600 hover:text-green-600 hover:bg-gradient-to-b hover:from-green-50/50 hover:to-transparent'
                }`}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-green-400/10 to-emerald-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <span className="text-lg sm:text-xl transition-transform duration-300 group-hover:scale-110 relative z-10">💰</span>
                <span className="text-xs sm:text-sm lg:text-base mt-0.5 sm:mt-0 sm:ml-1 relative z-10">Klick</span>
                
                {currentTab === 'clicker' && (
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full"></div>
                )}
              </button>
              <button
                onClick={() => { setCurrentTab('garden'); updatePlayerActivity(); }}
                className={`group px-1 sm:px-2 lg:px-3 py-2 sm:py-3 font-medium transition-all duration-300 border-b-3 text-xs sm:text-sm lg:text-base flex flex-col sm:flex-row items-center justify-center min-h-[48px] relative overflow-hidden ${
                  currentTab === 'garden'
                    ? 'border-pink-500 text-pink-700 bg-gradient-to-b from-pink-50 to-rose-100 shadow-inner'
                    : 'border-transparent text-gray-600 hover:text-pink-600 hover:bg-gradient-to-b hover:from-pink-50/50 hover:to-transparent'
                }`}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-pink-400/10 to-rose-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <span className="text-lg sm:text-xl transition-transform duration-300 group-hover:scale-110 relative z-10">🌺</span>
                <span className="text-xs sm:text-sm lg:text-base mt-0.5 sm:mt-0 sm:ml-1 relative z-10">Garten</span>
                
                {currentTab === 'garden' && (
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-gradient-to-r from-pink-400 to-rose-500 rounded-full"></div>
                )}
              </button>
              <button
                onClick={() => { setCurrentTab('shop'); updatePlayerActivity(); }}
                className={`group px-1 sm:px-2 lg:px-3 py-2 sm:py-3 font-medium transition-all duration-300 border-b-3 text-xs sm:text-sm lg:text-base flex flex-col sm:flex-row items-center justify-center min-h-[48px] relative overflow-hidden ${
                  currentTab === 'shop'
                    ? 'border-blue-500 text-blue-700 bg-gradient-to-b from-blue-50 to-cyan-100 shadow-inner'
                    : 'border-transparent text-gray-600 hover:text-blue-600 hover:bg-gradient-to-b hover:from-blue-50/50 hover:to-transparent'
                }`}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-400/10 to-cyan-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <span className="text-lg sm:text-xl transition-transform duration-300 group-hover:scale-110 relative z-10">🛒</span>
                <span className="text-xs sm:text-sm lg:text-base mt-0.5 sm:mt-0 sm:ml-1 relative z-10">Shop</span>
                
                {currentTab === 'shop' && (
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-gradient-to-r from-blue-400 to-cyan-500 rounded-full"></div>
                )}
              </button>
              
              <button
                onClick={() => { setCurrentTab('powerups'); updatePlayerActivity(); }}
                className={`group px-1 sm:px-2 lg:px-3 py-2 sm:py-3 font-medium transition-all duration-300 border-b-3 text-xs sm:text-sm lg:text-base flex flex-col sm:flex-row items-center justify-center min-h-[48px] relative overflow-hidden ${
                  currentTab === 'powerups'
                    ? 'border-purple-500 text-purple-700 bg-gradient-to-b from-purple-50 to-violet-100 shadow-inner'
                    : 'border-transparent text-gray-600 hover:text-purple-600 hover:bg-gradient-to-b hover:from-purple-50/50 hover:to-transparent'
                }`}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-purple-400/10 to-violet-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <span className="text-lg sm:text-xl transition-transform duration-300 group-hover:scale-110 relative z-10">⚡</span>
                <span className="text-xs sm:text-sm lg:text-base mt-0.5 sm:mt-0 sm:ml-1 relative z-10">Power</span>
                
                {currentTab === 'powerups' && (
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-gradient-to-r from-purple-400 to-violet-500 rounded-full"></div>
                )}
              </button>

              <button
                onClick={() => { setCurrentTab('prestige'); updatePlayerActivity(); }}
                className={`group px-1 sm:px-2 lg:px-3 py-2 sm:py-3 font-medium transition-all duration-300 border-b-3 relative text-xs sm:text-sm lg:text-base flex flex-col sm:flex-row items-center justify-center min-h-[48px] ${
                  currentTab === 'prestige'
                    ? 'border-yellow-500 text-yellow-700 bg-gradient-to-b from-yellow-50 to-amber-100 shadow-inner'
                    : 'border-transparent text-gray-600 hover:text-yellow-600 hover:bg-gradient-to-b hover:from-yellow-50/50 hover:to-transparent'
                }`}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/10 to-amber-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 overflow-hidden"></div>
                
                <span className="text-lg sm:text-xl transition-transform duration-300 group-hover:scale-110 relative z-10">⭐</span>
                <span className="text-xs sm:text-sm lg:text-base mt-0.5 sm:mt-0 sm:ml-1 relative z-10">Prestige</span>
                
                {currentTab === 'prestige' && (
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full"></div>
                )}
                
                {(prestige?.level || 0) > 0 && (
                  <div className="absolute top-0 -right-1 bg-gradient-to-r from-yellow-500 to-amber-600 text-white text-xs rounded-full w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5 flex items-center justify-center shadow-lg animate-pulse z-20">
                    {prestige?.level}
                  </div>
                )}
              </button>

              <button
                onClick={() => { setCurrentTab('companions'); updatePlayerActivity(); }}
                className={`group px-1 sm:px-2 lg:px-3 py-2 sm:py-3 font-medium transition-all duration-300 border-b-3 relative text-xs sm:text-sm lg:text-base flex flex-col sm:flex-row items-center justify-center min-h-[48px] ${
                  currentTab === 'companions'
                    ? 'border-orange-500 text-orange-700 bg-gradient-to-b from-orange-50 to-amber-100 shadow-inner'
                    : 'border-transparent text-gray-600 hover:text-orange-600 hover:bg-gradient-to-b hover:from-orange-50/50 hover:to-transparent'
                }`}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-400/10 to-amber-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 overflow-hidden"></div>
                
                <span className="text-lg sm:text-xl transition-transform duration-300 group-hover:scale-110 relative z-10">🐾</span>
                <span className="text-xs sm:text-sm lg:text-base mt-0.5 sm:mt-0 sm:ml-1 relative z-10">Team</span>
                
                {currentTab === 'companions' && (
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-gradient-to-r from-orange-400 to-amber-500 rounded-full z-0"></div>
                )}
                
                {companions.discovered.length > 0 && (
                  <div className="absolute top-0 -right-1 bg-gradient-to-r from-orange-500 to-amber-600 text-white text-xs rounded-full w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5 flex items-center justify-center shadow-lg animate-pulse z-20">
                    {companions.discovered.length}
                  </div>
                )}
              </button>

              <button
                onClick={() => { setCurrentTab('premium'); updatePlayerActivity(); }}
                className={`group px-1 sm:px-2 lg:px-3 py-2 sm:py-3 font-medium transition-all duration-300 border-b-3 relative text-xs sm:text-sm lg:text-base flex flex-col sm:flex-row items-center justify-center min-h-[48px] ${
                  currentTab === 'premium'
                    ? 'border-purple-500 text-purple-700 bg-gradient-to-b from-purple-50 via-pink-50 to-violet-100 shadow-inner'
                    : 'border-transparent text-gray-600 hover:text-purple-600 hover:bg-gradient-to-b hover:from-purple-50/50 hover:to-pink-50/50'
                }`}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-purple-400/10 via-pink-400/10 to-violet-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 overflow-hidden"></div>
                
                <span className="text-lg sm:text-xl transition-transform duration-300 group-hover:scale-110 relative z-10">💎</span>
                <span className="text-xs sm:text-sm lg:text-base mt-0.5 sm:mt-0 sm:ml-1 relative z-10">Premium</span>
                
                {currentTab === 'premium' && (
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-gradient-to-r from-purple-400 via-pink-400 to-violet-500 rounded-full"></div>
                )}
                
                <div className="absolute top-0 -right-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs rounded-full w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5 flex items-center justify-center animate-pulse shadow-lg z-20">
                  €
                </div>
              </button>


            </div>
          </div>
        </nav>
        
        {/* Level-Fortschrittsbalken als Teil der sticky Navigation */}
        <LevelProgressBar />
      </div>

      {/* Mobile Sticky Player Stats - immer sichtbar auf Mobile */}
      <motion.div 
        className="sm:hidden sticky top-[72px] z-40 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{ 
          transform: 'translateY(0)',
          transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out',
          height: 'auto',
          overflow: 'visible',
          opacity: '1'
        }}
        id="mobile-sticky-stats"
      >
        <div className="container mx-auto px-2 py-0">
          <div className="bg-white/95 backdrop-blur-sm rounded-t-lg shadow-sm p-1.5">
            {/* Kompakte Mobile Ressourcen-Anzeige */}
            <div className="grid grid-cols-3 gap-1.5">
              <div className="bg-yellow-50 rounded-lg p-1.5 text-center">
                <div className="text-xs mb-0.5">💰</div>
                <div className="text-xs font-medium text-gray-600">Münzen</div>
                <div className="text-xs font-bold text-yellow-700 truncate">{formatNumber(coins || 0)}</div>
              </div>
              
              <div className={`bg-purple-50 rounded-lg p-1.5 text-center transition-colors ${
                isGemVideoAvailable 
                  ? 'cursor-pointer hover:bg-purple-100' 
                  : 'opacity-60 cursor-not-allowed'
              }`} 
              onClick={isGemVideoAvailable ? handleGemClick : undefined} 
              title={isGemVideoAvailable ? "Werbevideo für 500 Edelsteine ansehen" : `Nächstes Video in ${getCooldownTime()} verfügbar`}>
                <div className={`text-xs mb-0.5 ${isGemVideoAvailable ? 'animate-pulse' : ''}`}>💎</div>
                <div className="text-xs font-medium text-gray-600">Edelsteine</div>
                <div className="text-xs font-bold text-purple-700 truncate">{formatNumber(gems || 0)}</div>
              </div>
              
              <div className="bg-blue-50 rounded-lg p-1.5 text-center">
                <div className="text-xs mb-0.5">🔄</div>
                <div className="text-xs font-medium text-gray-600">Pro Sek.</div>
                <div className="text-xs font-bold text-blue-700 truncate">{formatNumber(getTotalCoinsPerSecond() || 0)}</div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Main Content */}
      <main className="container mx-auto px-2 sm:px-4 py-4 sm:py-6 lg:py-8 transition-all duration-300 flex-1" id="main-content">
        <div className="max-w-6xl mx-auto">
          {/* Player Stats - nur im Garden-Tab (clicker) angezeigt */}
          {currentTab === 'clicker' && (
            <div className="block">
              <PlayerStats onOpenSettings={() => setShowSettings(true)} onOpenStatsOverlay={() => setShowStatsOverlay(true)} handleGemClick={handleGemClick} />
            </div>
          )}
          
          <motion.div
            key={currentTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {currentTab === 'clicker' && <ClickerSection />}
            {currentTab === 'garden' && <GardenSection />}
            {currentTab === 'shop' && <UpgradeShop />}
            {currentTab === 'powerups' && (
              <div className="space-y-6">
                <PowerUpShop />
                <PremiumAutoClicker />
              </div>
            )}
            {currentTab === 'prestige' && <PrestigeSection />}
            {currentTab === 'companions' && (
              <CompanionManager 
                showFreePremiumSpin={showFreePremiumSpin}
                onFreePremiumSpinComplete={handleFreePremiumSpinComplete}
              />
            )}
            {currentTab === 'premium' && <PremiumShop />}
          </motion.div>
        </div>
      </main>

      {/* Einstellungen Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 modal-container">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto modal-content">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-3 sm:p-4 flex justify-between items-center">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800">⚙️ Einstellungen</h2>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl sm:text-3xl font-bold min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation"
              >
                ×
              </button>
            </div>
            <div className="p-3 sm:p-6">
              <SettingsPage />
            </div>
          </div>
        </div>
      )}

      {/* 3-Gewinnt-Spiel Modal */}
      <MatchThreeGame
        isOpen={showMatchThree}
        onClose={() => setShowMatchThree(false)}
        level={matchThreeGame?.currentLevel || 1}
      />



      {/* Schwebender Klick-Button - Responsive Positionierung */}
      <div 
        className={`fixed sm:bottom-4 sm:left-4 bottom-20 left-1/2 transform sm:transform-none -translate-x-1/2 sm:translate-x-0 transition-opacity duration-300 ${
          showMatchThree || showSettings || currentTab === 'companions' || showStatsOverlay ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
        style={{ 
          position: 'fixed', 
          zIndex: 2147483647,
          pointerEvents: showMatchThree || showSettings || currentTab === 'companions' || showStatsOverlay ? 'none' : 'auto',
          display: 'block',
          visibility: showMatchThree || showSettings || currentTab === 'companions' || showStatsOverlay ? 'hidden' : 'visible'
        }}
      >
        <motion.div
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="relative"
        >
          {/* Klick-Effekt Animation - nur beim Klicken */}
          <motion.div
            className="absolute inset-0 rounded-full border-4 border-green-400 pointer-events-none opacity-30 sm:opacity-100"
            initial={{ scale: 0, opacity: 0 }}
            animate={clickPulseActive ? { 
              scale: [0, 1.5, 2], 
              opacity: [0, 0.4, 0] 
            } : { scale: 0, opacity: 0 }}
            transition={{ 
              duration: 0.3, 
              ease: "easeOut",
              repeat: 0
            }}
          />
          
          {/* Münzen-Counter - außerhalb des Buttons für volle Deckkraft */}
          <div className="absolute -top-2 -right-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded-full font-bold shadow-lg z-10">
            {formatNumber(coins)}
          </div>

          {/* Haupt-Klick-Button */}
          <motion.button
            onClick={() => {
              incrementClick();
              updatePlayerActivity();
              setClickPulseActive(true);
              setTimeout(() => setClickPulseActive(false), 300);
            }}
            className="relative bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white sm:w-16 sm:h-16 w-20 h-20 rounded-full shadow-2xl flex items-center justify-center sm:text-2xl text-3xl transition-all duration-300 border-2 border-white backdrop-blur-sm sm:opacity-100 opacity-70"
            whileHover={{ 
              boxShadow: "0 12px 24px rgba(34, 197, 94, 0.6)" 
            }}
            whileTap={{
              scale: 0.95,
              boxShadow: "0 8px 16px rgba(34, 197, 94, 0.4)"
            }}
            style={{ 
              minWidth: window.innerWidth < 640 ? 80 : 64, 
              minHeight: window.innerWidth < 640 ? 80 : 64,
              display: 'flex',
              position: 'relative'
            }}
          >
            💰
            
            {/* Pulsierender Hintergrund - nur beim Klicken */}
            <motion.div
              className="absolute inset-0 rounded-full bg-green-400 sm:opacity-20 opacity-10"
              animate={clickPulseActive ? { scale: [1, 1.3, 1] } : { scale: 1 }}
              transition={{ duration: 0.3, repeat: 0 }}
            />
          </motion.button>
          
                     {/* Tooltip */}
           <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 sm:left-0 sm:transform-none mb-2 bg-black text-white text-xs px-3 py-2 rounded-lg opacity-0 hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity duration-200 hidden sm:block shadow-lg">
             💰 Münzen sammeln
             <div className="absolute top-full left-1/2 transform -translate-x-1/2 sm:left-2 sm:transform-none w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black"></div>
           </div>
                 </motion.div>
       </div>

      {/* Schwebender Match-3 Button - Nur im Premium-Menü sichtbar */}
      {currentTab === 'premium' && (
        <div 
          className={`fixed bottom-4 right-4 transition-opacity duration-300 ${
            showMatchThree ? 'opacity-0 pointer-events-none' : 'opacity-100'
          }`}
          style={{ 
            position: 'fixed', 
            zIndex: 2147483647,
            bottom: 16, 
            right: 16,
            pointerEvents: showMatchThree ? 'none' : 'auto',
            display: 'block',
            visibility: showMatchThree ? 'hidden' : 'visible'
          }}
        >
          <motion.div
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="relative"
          >
            {/* Ring Animation */}
            {matchThreeRingActive && (
              <motion.div
                className="absolute inset-0 rounded-full border-4 border-purple-400 pointer-events-none"
                initial={{ scale: 1, opacity: 1 }}
                animate={{ 
                  scale: [1, 1.5, 2], 
                  opacity: [1, 0.7, 0] 
                }}
                transition={{ 
                  duration: 2, 
                  ease: "easeOut",
                  repeat: Infinity,
                  repeatDelay: 1
                }}
              />
            )}
            
            {/* Haupt-Button */}
            <motion.button
              onClick={() => setShowMatchThree(true)}
              className="relative bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white w-16 h-16 rounded-full shadow-2xl flex items-center justify-center text-2xl transition-all duration-300 border-2 border-white backdrop-blur-sm"
              animate={matchThreeRingActive ? { 
                boxShadow: ["0 8px 16px rgba(168, 85, 247, 0.4)", "0 12px 24px rgba(168, 85, 247, 0.6)", "0 8px 16px rgba(168, 85, 247, 0.4)"] 
              } : {
                boxShadow: "0 8px 16px rgba(0, 0, 0, 0.3)"
              }}
              transition={{ duration: 0.5 }}
              style={{ 
                minWidth: 64, 
                minHeight: 64,
                display: 'flex',
                position: 'relative'
              }}
            >
              🍎
              
              {/* Pulsierender Hintergrund */}
              <motion.div
                className="absolute inset-0 rounded-full bg-purple-400 opacity-20"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </motion.button>
            
            {/* Tooltip - Immer sichtbar auf Desktop */}
            <div className="absolute bottom-full right-0 mb-2 bg-black text-white text-xs px-3 py-2 rounded-lg opacity-0 hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity duration-200 hidden sm:block shadow-lg">
              🎮 Garten-Match spielen
              <div className="absolute top-full right-2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black"></div>
            </div>
          </motion.div>
        </div>
      )}




      
      {/* Hintergrundmusik */}
      <div className={showMatchThree ? 'hidden' : 'block'}>
        <BackgroundMusic />
      </div>
      
      {/* Statistiken Overlay */}
      <StatsOverlay 
        isOpen={showStatsOverlay} 
        onToggle={() => setShowStatsOverlay(!showStatsOverlay)} 
        onOpenSettings={() => setShowSettings(true)}
      />
      
      {/* Footer */}
      <Footer />
      
      {/* Performance Monitor für hohe Level */}
      <PerformanceMonitor />

      {/* Video-Reward-Modal für Edelsteine (zentral, damit kein doppeltes Modal) */}
      <VideoRewardModal
        isOpen={showGemVideo}
        onClose={() => setShowGemVideo(false)}
        onComplete={handleGemVideoComplete}
        customTitle="💎 Gratis-Edelsteine"
        customDescription={`Schaue ein kurzes Werbevideo und erhalte ${formatNumber(calculateGemReward())} Edelsteine als Belohnung!`}
        customRewardText={`+${formatNumber(calculateGemReward())} Edelsteine erhalten!`}
        rewardType="gems"
      />

      {/* Leaderboard Modal */}
      {showLeaderboard && (
        <Leaderboard />
      )}
    </div>
  );
}

export default App;
